import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Screen } from "@/components/layout/Screen";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/interactions/EmptyState";
import { LoadingState } from "@/components/interactions/LoadingState";
import { getAuthenticatedClient, getCurrentUserId } from "@/services/client";
import { cn } from "@/lib/utils";
import { Bird, ChevronRight, Calendar } from "lucide-react";
import { format } from "date-fns";

interface BatchHistoryRow {
  id: string;
  batch_code: string | null;
  placement_date: string;
  actual_harvest_date: string | null;
  batch_status: string;
  current_bird_count: number | null;
  doc_count: number;
  current_fcr: number | null;
  cumulative_mortality: number | null;
}

export const BatchHistoryScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const userId = getCurrentUserId();

  const { data: batches, isLoading } = useQuery({
    queryKey: ["cbs", "batch-history", userId],
    queryFn: async () => {
      if (!userId) return [];
      const client = getAuthenticatedClient();
      const { data, error } = await client
        .from("batches")
        .select("id, batch_code, placement_date, actual_harvest_date, batch_status, current_bird_count, doc_count, current_fcr, cumulative_mortality")
        .eq("user_id", userId)
        .in("batch_status", ["completed", "abandoned"])
        .order("actual_harvest_date", { ascending: false });
      if (error) throw error;
      return (data ?? []) as BatchHistoryRow[];
    },
    enabled: !!userId,
  });

  return (
    <Screen showTopBar showBottomNav topBarProps={{ title: t("cbs.batchHistory.title", { defaultValue: "Batch History" }) }}>
      <div className="p-4 space-y-3">
        {isLoading ? (
          <LoadingState />
        ) : !batches || batches.length === 0 ? (
          <EmptyState
            icon={Bird}
            title={t("cbs.batchHistory.empty", { defaultValue: "No completed batches yet" })}
            description={t("cbs.batchHistory.emptyDesc", { defaultValue: "Complete your first batch to see it here" })}
          />
        ) : (
          batches.map((batch, index) => (
            <motion.div
              key={batch.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: index * 0.05 }}
            >
              <Card
                interactive
                className="p-4"
                onClick={() => navigate(`/batch/${batch.id}`)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                      <Bird className="w-5 h-5 text-foreground" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">
                        {batch.batch_code || `Batch ${batch.id.slice(0, 6)}`}
                      </p>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Calendar className="w-3 h-3" />
                        <span>
                          {format(new Date(batch.placement_date), "dd MMM")}
                          {batch.actual_harvest_date && ` → ${format(new Date(batch.actual_harvest_date), "dd MMM yyyy")}`}
                        </span>
                      </div>
                    </div>
                  </div>

                  <Badge
                    className={cn(
                      "text-xs",
                      batch.batch_status === "completed"
                        ? "bg-success/15 text-success border-success/30"
                        : "bg-destructive/15 text-destructive border-destructive/30"
                    )}
                    variant="outline"
                  >
                    {batch.batch_status === "completed"
                      ? `✅ ${t("cbs.batch.status.completed", { defaultValue: "Completed" })}`
                      : `❌ ${t("cbs.batch.status.abandoned", { defaultValue: "Abandoned" })}`}
                  </Badge>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex gap-4 text-xs text-muted-foreground">
                    <span>
                      <span className="font-medium text-foreground">{batch.current_bird_count ?? batch.doc_count}</span> birds
                    </span>
                    {batch.current_fcr != null && (
                      <span>
                        FCR <span className="font-medium text-foreground">{batch.current_fcr.toFixed(2)}</span>
                      </span>
                    )}
                    {batch.cumulative_mortality != null && (
                      <span>
                        Mort <span className="font-medium text-foreground">{batch.cumulative_mortality}</span>
                      </span>
                    )}
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </div>
              </Card>
            </motion.div>
          ))
        )}
      </div>
    </Screen>
  );
};
